import { ComplexBundle } from "../types/Product.ts";

export type Selections = Record<string, number | null | number[]>;

/**
 * Check if a single condition is matched (basically, 'did this question have this answer?')
 */
const doesSingleConditionMatch = (
  condition: ComplexBundle.Condition,
  selections: Selections,
) => {
  const entryKey = `${condition.step_dependency_index}-${condition.entry_dependency_index}`;
  if (Array.isArray(selections[entryKey])) {
    return selections[entryKey].includes(
      Number(condition.option_dependency_index),
    );
  }
  return selections[entryKey] === Number(condition.option_dependency_index);
};

/**
 * Check if a condition group is matched.
 */
const doesConditionGroupMatch = (
  group: ComplexBundle.ConditionGroup,
  selections: Selections,
) => {
  let anyConditionMatched: boolean = false;

  for (const [index, condition] of group.conditions.entries()) {
    if (doesSingleConditionMatch(condition, selections)) {
      if (
        condition.condition_combiner === "AND" &&
        !anyConditionMatched &&
        index > 0
      ) {
        // This means that the right side of the AND matched but the left side didn't
        return false;
      }
      anyConditionMatched = true;
    } else if (condition.condition_combiner === "AND") {
      // This means the right side of the AND failed
      return false;
    }
  }

  return anyConditionMatched;
};

/**
 * Checks if the set of provided condition groups match.
 *
 * Conditions can be combined with "AND" or "OR" at two layers (within a group and between
 * groups). Both can be used together, but there's no operator precedence, they are
 * evaluated in order. For example,
 *   A OR B AND C OR D
 * will be evaluated as:
 *   ((A OR B) AND C) OR D
 *
 * @TODO It's not currently clear if this is the same behaviour as the legacy website.
 */
export const doConditionGroupsMatch = (
  groups: ComplexBundle.ConditionGroup[],
  selections: Selections,
) => {
  let anyConditionGroupMatched: boolean = false;

  for (const [index, group] of groups.entries()) {
    const metConditions = doesConditionGroupMatch(group, selections);

    if (metConditions) {
      if (
        group.condition_group_combiner === "AND" &&
        !anyConditionGroupMatched &&
        index > 0
      ) {
        // This means that the right side of the AND matched but the left side didn't
        return false;
      }
      anyConditionGroupMatched = true;
    } else if (group.condition_group_combiner === "AND") {
      // This means the right side of the AND failed
      return false;
    }
  }

  return anyConditionGroupMatched;
};

/**
 * Checks if an option should be shown based on other selections made by the user.
 */
export const shouldOptionBeShown = (
  option: ComplexBundle.Option,
  selections: Selections,
) => {
  // Always return true if the option isn't using conditional visibility
  if (option.enable_conditions !== "on" || !option.product_condition_groups) {
    return true;
  }

  const matched = doConditionGroupsMatch(
    option.product_condition_groups,
    selections,
  );

  // There are two types of conditional visibility, "Show if..." and "Hide if..."
  if (matched && option.entry_visibility === "hide") {
    return false;
  } else if (!matched && option.entry_visibility === "show") {
    return false;
  }

  return true;
};

/**
 * Checks if a whole field should be shown based on earlier selections. Field conditions
 * may reference earlier steps or earlier fields within their own step, so the caller must
 * pass selections built by activeStructure's ordered pass. Must stay in sync with
 * BundleDataParser::should_entry_be_shown.
 */
export const shouldEntryBeShown = (
  entry: ComplexBundle.Entry,
  selections: Selections,
) => {
  if (
    entry.enable_entry_conditions !== "on" ||
    !entry.entry_condition_groups?.length
  ) {
    return true;
  }

  const matched = doConditionGroupsMatch(
    entry.entry_condition_groups,
    selections,
  );
  const visibility = entry.entry_visibility || "show";

  if (matched && visibility === "hide") {
    return false;
  }
  if (!matched && visibility === "show") {
    return false;
  }

  return true;
};

/**
 * Checks if a whole step should be shown based on selections in earlier steps.
 *
 * Step conditions may only reference earlier steps (enforced by the Bundle Manager), so
 * they can be evaluated in one forward pass. The server cart routes apply the same rule,
 * so this must stay in sync with BundleDataParser::should_step_be_shown.
 */
export const shouldStepBeShown = (
  step: ComplexBundle.Step,
  selections: Selections,
) => {
  if (
    step.enable_step_conditions !== "on" ||
    !step.step_condition_groups?.length
  ) {
    return true;
  }

  const matched = doConditionGroupsMatch(
    step.step_condition_groups,
    selections,
  );
  const visibility = step.step_visibility || "show";

  if (matched && visibility === "hide") {
    return false;
  }
  if (!matched && visibility === "show") {
    return false;
  }

  return true;
};
